/**
 * View component for /admin/authoring/scenes
 *
 * Generic scene list view. Defaults to 'all' with:
 * this.props.dispatch(sceneActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as sceneActions from '../sceneActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import util components
import routeUtils from "../../../global/utils/routeUtils";

// import scene components
import AdminSceneListItem from '../components/AdminSceneListItem.js.jsx';

class AdminSceneList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      page: '1'
      , per: '50'
      , queryText: ''
      , sortBy: 'title'
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(sceneActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(this.state)));
  }

  _handleSort(sortBy) {
    const { dispatch } = this.props;
    let newState = _.cloneDeep(this.state);
    newState.sortBy = sortBy;
    newState.page = "1"; // reset our pagination
    this.setState(newState);
    dispatch(sceneActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newState)));
  }

  _setPerPage(per) {
    const { dispatch } = this.props;
    let newState = _.cloneDeep(this.state);
    newState.page = '' + 1;
    newState.per = '' + per
    this.setState(newState);
    dispatch(sceneActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newState)));
  }

  _handleSetPagination(newPagination) {
    const { dispatch } = this.props;
    let newState = _.cloneDeep(this.state);
    newState.page = '' + newPagination.page;
    newState.per = '' + newPagination.per
    this.setState(newState);
    dispatch(sceneActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newState)));
  }

  _handleQuery(e) {
    const { dispatch } = this.props;
    let newState = _.cloneDeep(this.state);
    newState.queryText = e.target.value.toLowerCase();
    newState.page = "1"; // reset our pagination
    this.setState(newState);
    dispatch(sceneActions.fetchListIfNeeded("search", routeUtils.queryStringFromObject(newState)));
  }

  render() {
    const { location, sceneStore } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */
    const queryString = routeUtils.queryStringFromObject(this.state);
    const scenes = sceneStore.util.getList("search", queryString);
    const sceneList = sceneStore.lists.search ? sceneStore.lists.search[queryString] : {};

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !sceneList || !sceneList.items || sceneList.didInvalidate;

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <h1> Scene List
                <Link className="yt-btn create u-pullRight" to={'/admin/authoring/scenes/new'}> 
                  <img className="-icon" src={'/img/create-white.png'}></img>
                </Link>
              </h1>
              <hr/>
              <div className="yt-toolbar">
                <div className="yt-tools right">
                  <div className="search">
                    <SearchInput
                      change={this._handleQuery}
                      name="query"
                      placeholder="Search..."
                      required={false}
                      value={this.state.queryText}
                      />
                  </div>
                </div>
            </div>
            { isEmpty ?
              (
                sceneList && sceneList.isFetching ?
                <div className="u-centerText">
                  <div className="loading"></div>
                </div>
                :
                <h2>Empty.</h2>
              )
              :
              <div style={{ opacity: sceneList.isFetching ? 0.5 : 1 }}>
                <div className="content-container">
                  <div className="table-wrapper">
                    <table className="yt-table striped">
                      <caption>
                        { sceneList.filter && sceneList.filter.queryText && sceneList.filter.queryText.length > 0 ?
                          <span>Filtered Scenes &mdash; {sceneList.items.length} of {sceneList.pagination.total}</span>
                          :
                          <span>All Scenes &mdash; {sceneList.pagination ? sceneList.pagination.total : 0}</span>
                        }
                        <div className="per-page-select u-pullRight">
                          <label>Show per page: </label>
                          <select
                            name="numPerPage"
                            onChange={(e) => this._setPerPage(e.target.value)}
                            value={this.state.per}
                            >
                            <option value={25}> 25 </option>
                            <option value={50}> 50 </option>
                            <option value={100}> 100 </option>
                          </select>
                        </div>
                      </caption>
                      <thead>
                        <tr>
                          <th className="_10">Poster URL</th>
                          <th className="_70 sortable" onClick={()=> this._handleSort(sceneList.filter && sceneList.filter.sortBy === 'title' ? '-title' : 'title')}>
                            Title
                            {sceneList.filter && sceneList.filter.sortBy === 'title' ?
                              <i className="ion ion-ios-arrow-down" />
                              : sceneList.filter && sceneList.filter.sortBy === '-title' ?
                              <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                          <th className="_10 sortable" onClick={()=> this._handleSort(sceneList.filter && sceneList.filter.sortBy === 'updated' ? '-updated' : 'updated')}>
                            Last Modified
                            {sceneList.filter && sceneList.filter.sortBy === 'updated' ?
                              <i className="ion ion-ios-arrow-down" />
                              : sceneList.filter && sceneList.filter.sortBy === '-updated' ?
                              <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          scenes.map((scene, i) =>
                          <AdminSceneListItem
                            key={i}
                            scene={scene}
                            />
                        )
                      }
                    </tbody>
                  </table>
                  { sceneList.items.length === 0 ?
                    null
                    :
                    <Pagination
                      classes="u-centerText"
                      totalPages={sceneList.pagination ? Math.floor(sceneList.pagination.total / sceneList.pagination.per) + 1 : 1}
                      pagination={sceneList.pagination ? sceneList.pagination : {page: 1, per: 50}}
                      setPagination={this._handleSetPagination}
                    />
                  }
                </div>
              </div>
            </div>
          }
          </div>
        </section>
      </div>
      </AuthoringLayout>
    )
  }
}

AdminSceneList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    sceneStore: store.scene
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSceneList)
);



// WEBPACK FOOTER //
// modules/scene/views/AdminSceneList.js.jsx